import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import Table from 'components/Table';
import Error from '../Common/Error';
import {
  SubmittedCashBalance,
  SubmittedShareholding,
} from './InvestmentTransferForm';
import useStyles from './styles';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { sumBy } from 'lodash';
import { useHistory } from 'react-router-dom';
import api from 'lib/trpcClient';

type SummaryProps = {
  recipientId?: string;
  sourceInvestorId: string;
  shareholdings: Array<SubmittedShareholding>;
  cashBalances: Array<SubmittedCashBalance>;
  onSubmit: () => void;
  isCreatingInvestmentTransfer: boolean;
};

const columns = [
  {
    label: 'Tranche',
    key: 'fund',
    render: (shareholding: SubmittedShareholding) =>
      shareholding.fund?.fundName,
  },
  {
    label: 'Company',
    key: 'company',
    render: (shareholding: SubmittedShareholding) =>
      `${shareholding.company?.tradingName} ${shareholding.company?.shareClass}`,
  },
  {
    label: 'Initial share price',
    key: 'initialPrice',
    render: (shareholding: SubmittedShareholding) =>
      numberToCurrencyString(shareholding.initialSharePrice ?? 0),
  },
  {
    label: 'Initial subscription',
    key: 'subscriptionAmount',
    render: (shareholding: SubmittedShareholding) =>
      numberToCurrencyString(shareholding.subscriptionAmount),
  },
  {
    label: 'Current share price',
    key: 'currentSharePrice',
    render: (shareholding: SubmittedShareholding) =>
      numberToCurrencyString(shareholding.currentSharePrice),
  },
  {
    label: 'Current share value',
    key: 'currentShareValue',
    render: (shareholding: SubmittedShareholding) =>
      numberToCurrencyString(shareholding.currentShareValue),
  },
  {
    label: 'Shares to transfer',
    key: 'sharesToTransfer',
    render: (shareholding: SubmittedShareholding) =>
      numberToDisplayString(shareholding.sharesToTransfer),
  },
  {
    label: 'Value to transfer',
    key: 'valueToTransfer',
    render: (shareholding: SubmittedShareholding) =>
      numberToCurrencyString(
        shareholding.sharesToTransfer * (shareholding.currentSharePrice ?? 0),
      ),
  },
];

const Summary: React.FC<SummaryProps> = ({
  recipientId,
  sourceInvestorId,
  shareholdings,
  cashBalances,
  onSubmit,
  isCreatingInvestmentTransfer,
}) => {
  const { classes } = useStyles();
  const history = useHistory();
  const theme = useTheme();

  const recipient = api.investor.getInvestor.useQuery(
    { investorId: recipientId ?? '' },
    { enabled: !!recipientId },
  );
  const sourceInvestor = api.investor.getInvestor.useQuery({
    investorId: sourceInvestorId,
  });

  if (!recipientId) {
    return <Error message="You have to select recipient." />;
  }

  if (recipient.isLoading || sourceInvestor.isLoading) {
    return (
      <Box display="flex" justifyContent="center" width="100%">
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Stack direction="column" spacing={4}>
      <Typography variant="h1" color="textSecondary">
        Transfer summary
      </Typography>
      <Typography variant="h3" color="textSecondary">
        Please check and confirm the below transfer(s)
      </Typography>
      <Typography variant="body2" color="textSecondary">
        <strong>Please note:</strong> When transfers are made: (i) past and
        future fees; (ii) exits; and (iii) cash held for fees, will each move to
        the transferee on a pro rata basis, calculated as the initial value of
        the transferring shares as a percentage of the transferor subscription’s
        initial investable capital.
      </Typography>
      <Stack direction="column" spacing={0}>
        <Typography
          variant="h6"
          className={classes.tableHeader}
          component="div"
        >
          {`Transfer(s) from ${sourceInvestor.data?.fullName} (${sourceInvestor.data?.email}) to ${recipient.data?.fullName} (${recipient.data?.email})`}
        </Typography>
        <Table
          columns={columns}
          tablebody={shareholdings}
          hideEmptyMessage
          TableFooter={
            <tfoot>
              {cashBalances.map((cashBalance) => (
                <tr
                  key={cashBalance.firm?._id}
                  className={classes.summaryCashBalanceRow}
                >
                  <td colSpan={7} align="right">
                    Transferring cash balance
                  </td>
                  <td>
                    {numberToCurrencyString(cashBalance.balanceToTransfer)}
                    {cashBalances.length > 1
                      ? ` (${cashBalance.firm?.firmName})`
                      : null}
                  </td>
                </tr>
              ))}
              <tr className={classes.tableFooter}>
                <td colSpan={7}>
                  <Typography variant="h6" component="div" align="right">
                    Total value to transfer
                  </Typography>
                </td>
                <td>
                  <Typography variant="h6" component="div">
                    {numberToCurrencyString(
                      sumBy(cashBalances, 'balanceToTransfer') +
                        sumBy(
                          shareholdings,
                          (shareholding) =>
                            shareholding.sharesToTransfer *
                            (shareholding.currentSharePrice ?? 0),
                        ),
                    )}
                  </Typography>
                </td>
              </tr>
            </tfoot>
          }
        />
      </Stack>
      <Box gap={theme.spacing(4)} display="flex" justifyContent="flex-end">
        <Button
          variant="outlined"
          color="primary"
          onClick={() => history.goBack()}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          disabled={isCreatingInvestmentTransfer}
        >
          Select notification options
        </Button>
      </Box>
    </Stack>
  );
};

export default Summary;
